/* eslint-disable no-console */
import { User, Profile, UserManager, WebStorageStateStore, UserManagerSettings } from "oidc-client";
import { ApplicationPaths } from "./ApiAuthorizationConstants";
import { IConfig } from "../api/AuthorizedApiBase";
import { AppConfigurationService, ResultType } from "../api/APIClient";

export const AuthenticationResultStatus = {
    Redirect: "redirect",
    Success: "success",
    Fail: "fail",
};

interface ICallbacks {
    callback: () => void;
    subscription: number;
}

interface IReturnStatus {
    status: string;
    message: string;
    returnUrl: string;
}

export class AuthorizeService {
    private static createArguments(state) {
        return { useReplaceToNavigate: true, data: state };
    }

    private static error(message: string): IReturnStatus {
        return { status: AuthenticationResultStatus.Fail, message: "", returnUrl: "" };
    }

    private static success(state: string): IReturnStatus {
        return { status: AuthenticationResultStatus.Success, message: "", returnUrl: "" };
    }

    private static redirect(): IReturnStatus {
        return { status: AuthenticationResultStatus.Redirect, message: "", returnUrl: "" };
    }

    private callbacks: ICallbacks[] = [];

    private nextSubscriptionId = 0;

    private user: User | null = null;

    private authenticated = false;

    // By default pop ups are disabled because they don't work properly on Edge.
    // If you want to enable pop up authentication simply set this flag to false.
    private popUpDisabled = true;

    private userManager: UserManager | undefined;

    async getUser(): Promise<User | null> {
        if (this.user) {
            return this.user;
        }

        const userManager = await this.getUserManager();
        const user = await userManager.getUser();
        return user;
    }

    async isAuthenticated(): Promise<boolean> {
        const user = await this.getUser();
        return !!user && !user.expired;
    }

    async getProfile(): Promise<Profile | null> {
        if (this.user && this.user.profile) {
            return this.user.profile;
        }

        const user = await this.getUser();
        return user && user.profile;
    }

    async getAccessToken(): Promise<string | null> {
        const user = await this.getUser();
        return user && user.access_token;
    }

    // We try to authenticate the user in three different ways:
    // 1) We try to see if we can authenticate the user silently. This happens
    //    when the user is already logged in on the IdP and is done using a hidden iframe
    //    on the client.
    // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
    //    redirect flow.
    async signIn(returnUrl: string): Promise<IReturnStatus> {
        const userManager = await this.getUserManager();
        try {
            const silentUser = await userManager.signinSilent(
                AuthorizeService.createArguments(undefined)
            );
            this.updateState(silentUser);
            return AuthorizeService.success(returnUrl);
        } catch (silentError) {
            // User might not be authenticated, fallback to popup authentication
            console.log("Silent authentication error: ", silentError);

            try {
                if (this.popUpDisabled) {
                    throw new Error(
                        "Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it."
                    );
                }

                const popUpUser = await userManager.signinPopup(
                    AuthorizeService.createArguments(undefined)
                );
                this.updateState(popUpUser);
                return AuthorizeService.success(returnUrl);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (popUpError: any) {
                if (popUpError.message === "Popup window closed") {
                    // The user explicitly cancelled the login action by closing an opened popup.
                    return AuthorizeService.error("The user closed the window.");
                }
                if (!this.popUpDisabled) {
                    console.log("Popup authentication error: ", popUpError);
                }

                // PopUps might be blocked by the user, fallback to redirect
                try {
                    await userManager.signinRedirect(AuthorizeService.createArguments(returnUrl));
                    return AuthorizeService.redirect();
                } catch (redirectError) {
                    console.log("Redirect authentication error: ", redirectError);
                    return AuthorizeService.error(redirectError as string);
                }
            }
        }
    }

    async completeSignIn(url: string): Promise<IReturnStatus> {
        try {
            const userManager = await this.getUserManager();
            const user = await userManager.signinCallback(url);
            this.updateState(user);
            return AuthorizeService.success(user && user.state);
        } catch (error) {
            console.log("There was an error signing in: ", error);
            return AuthorizeService.error("There was an error signing in.");
        }
    }

    // We try to sign out the user in two different ways:
    // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
    //    post logout redirect flow.
    async signOut(returnUlr: string): Promise<IReturnStatus> {
        const userManager = await this.getUserManager();
        try {
            if (this.popUpDisabled) {
                throw new Error(
                    "Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it."
                );
            }

            await userManager.signoutPopup(AuthorizeService.createArguments(undefined));
            this.updateState(null);
            return AuthorizeService.success(returnUlr);
        } catch (popupSignOutError) {
            console.log("Popup signout error: ", popupSignOutError);
            try {
                await userManager.signoutRedirect(AuthorizeService.createArguments(returnUlr));
                return AuthorizeService.redirect();
            } catch (redirectSignOutError) {
                console.log("Redirect signout error: ", redirectSignOutError);
                return AuthorizeService.error(redirectSignOutError as string);
            }
        }
    }

    async completeSignOut(url: string): Promise<IReturnStatus> {
        const userManager = await this.getUserManager();
        try {
            const res = await userManager.signoutCallback(url);
            this.updateState(null);
            return AuthorizeService.success(res && res.state);
        } catch (error) {
            console.log(`There was an error trying to log out '${error}'.`);
            return AuthorizeService.error(error as string);
        }
    }

    private updateState(user: User | null): void {
        this.user = user;
        this.authenticated = !!this.user;
        this.notifySubscribers();
    }

    subscribe(callback: () => void): number {
        this.nextSubscriptionId += 1;
        this.callbacks.push({ callback, subscription: this.nextSubscriptionId });
        return this.nextSubscriptionId - 1;
    }

    unsubscribe(subscriptionId: number): void {
        const subscriptionIndex = this.callbacks
            .map((element, index) => ({ found: element.subscription === subscriptionId, index }))
            .filter((element) => element.found === true);
        if (subscriptionIndex.length !== 1) {
            throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
        }

        this.callbacks.splice(subscriptionIndex[0].index, 1);
    }

    private notifySubscribers(): void {
        for (let i = 0; i < this.callbacks.length; i++) {
            const { callback } = this.callbacks[i];
            callback();
        }
    }

    private async initUserManager(): Promise<UserManager> {
        const configService = new AppConfigurationService({
            getAuthorization: () => {
                return Promise.resolve<string>(""); // Annonymous endpoint
            },
        });
        const response = await configService.getIdentityConfiguration();
        if (response.resultType !== ResultType.Succeeded) {
            throw new Error(
                `Could not load settings of the identity provider. Message: "${response.errorDescription?.message}"`
            );
        }

        const { base_redirect_uri: baseUri, ...settings } = response.data;

        const userManagerProps: UserManagerSettings = {
            ...settings,
            post_logout_redirect_uri: `${baseUri}${ApplicationPaths.LogOutCallback}`,
            redirect_uri: `${baseUri}${ApplicationPaths.LoginCallback}`,
            userStore: new WebStorageStateStore({
                prefix: "BIMDigitalTwinControlPanel",
            }),
        };

        const userManager = new UserManager(userManagerProps);

        userManager.events.addUserSignedOut(async () => {
            await userManager.removeUser();
            this.updateState(null);
        });

        return userManager;
    }

    private async getUserManager(): Promise<UserManager> {
        if (this.userManager === undefined) {
            const userManager = await this.initUserManager();
            this.userManager = userManager;
        }
        return this.userManager;
    }

    async refreshToken(): Promise<void> {
        const silentUser = await (
            await this.getUserManager()
        ).signinSilent(AuthorizeService.createArguments(undefined));
        this.updateState(silentUser);
    }
}

export const authService = new AuthorizeService();

export const APIConfig: IConfig = {
    getAuthorization: async () => {
        const isAuthenticated = await authService.isAuthenticated();
        if (!isAuthenticated) {
            await authService.refreshToken();
        }
        const token = await authService.getAccessToken();
        return token !== null ? `Bearer ${token}` : "";
    },
};
